import { useCallback, useEffect, useState } from "react";
import { onConnectionChange } from "../api/connectionStatus";
import type { RequestQueue } from "./useRequestQueue";

interface UseApiResult<T> {
    data: T | null;
    loading: boolean;
    error: Error | null;
    refetch: () => void;
}

interface UseApiOptions {
    /** Delay in ms before executing (for simple staggering, not used with queue) */
    delayMs?: number;
    /** Request queue for serialized execution */
    queue?: RequestQueue;
    /** Queue item id — required when using queue */
    queueId?: string;
    /** Queue priority (lower = higher priority, default 10) */
    queuePriority?: number;
}

export function useApi<T>(
    fetcher: () => Promise<T>,
    deps: unknown[] = [],
    enabled = true,
    delayOrOptions: number | UseApiOptions = 0
): UseApiResult<T> {
    const opts: UseApiOptions = typeof delayOrOptions === "number" ? { delayMs: delayOrOptions } : delayOrOptions;

    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [refetchCount, setRefetchCount] = useState(0);

    const refetch = useCallback(() => {
        setRefetchCount((c) => c + 1);
    }, []);

    useEffect(() => {
        if (!enabled) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);

        const handleResult = (result: T) => {
            if (!cancelled) {
                setData(result);
                setLoading(false);
            }
        };
        const handleError = (err: unknown) => {
            if (!cancelled) {
                setData(null);
                setError(err instanceof Error ? err : new Error(String(err)));
                setLoading(false);
            }
        };

        if (opts.queue && opts.queueId) {
            const { promise, cancel } = opts.queue.enqueue(opts.queueId, fetcher, opts.queuePriority ?? 10);
            promise.then(handleResult).catch((err) => {
                // Ignore cancellation errors — they're expected when deps change
                if (!cancelled && !(err instanceof Error && err.message === "cancelled")) {
                    handleError(err);
                }
            });
            return () => {
                cancelled = true;
                cancel();
            };
        }

        const run = () => {
            fetcher().then(handleResult).catch(handleError);
        };

        if (opts.delayMs && opts.delayMs > 0) {
            const timer = setTimeout(run, opts.delayMs);
            return () => {
                cancelled = true;
                clearTimeout(timer);
            };
        }
        run();
        return () => {
            cancelled = true;
        };
    }, [...deps, refetchCount, enabled]);

    // Auto-refetch when connection is restored after being unreachable
    useEffect(() => {
        let wasUnreachable = false;
        return onConnectionChange((isUnreachable) => {
            if (wasUnreachable && !isUnreachable && error) {
                setRefetchCount((c) => c + 1);
            }
            wasUnreachable = isUnreachable;
        });
    }, [error]);

    return { data, loading, error, refetch };
}
